import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { money2, fmtDate } from '@/lib/helpers';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { PushToggle } from '@/components/PushToggle';
import { CalendarClock, CheckCircle2, RotateCcw, Loader2, AlarmClock, Send } from 'lucide-react';

const STATE_STYLE = {
  overdue: { pill: 'bg-destructive/15 text-destructive', dot: 'bg-destructive', label: 'Overdue' },
  today: { pill: 'bg-amber-500/15 text-amber-600', dot: 'bg-amber-500', label: 'Due today' },
  upcoming: { pill: 'bg-secondary text-muted-foreground', dot: 'bg-muted-foreground', label: 'Upcoming' },
  cleared: { pill: 'bg-emerald-500/15 text-emerald-600', dot: 'bg-emerald-500', label: 'Cleared' },
};

export default function DueReminders() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const [scope, setScope] = useState('open');
  const [digesting, setDigesting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['reminders', scope],
    queryFn: async () => (await api.get(`/reminders?scope=${scope}`)).data.data,
  });
  const items = data?.items || [];
  const counts = data?.counts || { overdue: 0, today: 0, upcoming: 0 };

  const setCleared = async (invoice_no, cleared) => {
    try {
      await api.post(`/sales/${invoice_no}/due-clear`, { cleared });
      toast.success(cleared ? 'Marked as cleared' : 'Reopened');
      qc.invalidateQueries({ queryKey: ['reminders'] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendDigest = async () => {
    setDigesting(true);
    try {
      const r = (await api.post('/reminders/digest/send')).data.data;
      toast.success(r.sent ? `Digest sent · ${r.total} due` : 'Digest processed');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDigesting(false); }
  };

  return (
    <Page>
      <TopBar title="Due & Reminders" showStore={false} />
      <div className="px-4 pt-4">
        <div className="mb-3"><PushToggle /></div>
        {/* Summary */}
        <div className="grid grid-cols-3 gap-2" data-testid="reminders-summary">
          <SummaryTile tone="overdue" label="Overdue" value={counts.overdue} />
          <SummaryTile tone="today" label="Due today" value={counts.today} />
          <SummaryTile tone="upcoming" label="Upcoming" value={counts.upcoming} />
        </div>

        {/* Scope tabs */}
        <div className="mt-4 flex items-center gap-2">
          {[['open', 'Open'], ['cleared', 'Cleared'], ['all', 'All']].map(([v, l]) => (
            <button key={v} onClick={() => setScope(v)} data-testid={`reminders-scope-${v}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium ${scope === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{l}</button>
          ))}
          {isOwner && (
            <Button variant="secondary" size="sm" className="ml-auto h-9 rounded-lg" onClick={sendDigest} disabled={digesting} data-testid="reminders-send-digest">
              {digesting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Send today&apos;s digest
            </Button>
          )}
        </div>

        <div className="mt-3 space-y-2 pb-8">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-12 text-center">
              <AlarmClock className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm font-medium">Nothing due here</p>
              <p className="mt-1 text-xs text-muted-foreground">Set a delivery/alteration due date when editing an invoice.</p>
            </div>
          ) : items.map((r) => {
            const st = STATE_STYLE[r.due_state] || STATE_STYLE.upcoming;
            return (
              <div key={r.invoice_no} className="rounded-2xl border border-border bg-card p-3" data-testid={`reminder-${r.invoice_no}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.pill}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
                      </span>
                      <button onClick={() => navigate(`/invoice/${r.invoice_no}`)} className="font-mono text-sm font-semibold underline-offset-2 hover:underline" data-testid={`reminder-open-${r.invoice_no}`}>{r.invoice_no}</button>
                    </div>
                    <p className="mt-1 truncate text-sm font-medium">{r.customer?.name || 'Walk-in'} {r.customer?.phone ? `· ${r.customer.phone}` : ''}</p>
                    <p className="text-xs text-muted-foreground">
                      <CalendarClock className="mr-1 inline h-3 w-3" />Due {fmtDate(r.due_date)} · {money2(r.net)} · {r.store_name || ''}{r.salesperson ? ` · ${r.salesperson}` : ''}
                    </p>
                    {r.notes && <p className="mt-1 truncate text-xs text-muted-foreground">Notes: {r.notes}</p>}
                  </div>
                  {r.due_cleared ? (
                    <Button size="sm" variant="secondary" className="h-9 shrink-0 rounded-lg" onClick={() => setCleared(r.invoice_no, false)} data-testid={`reminder-reopen-${r.invoice_no}`}>
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />Reopen
                    </Button>
                  ) : (
                    <Button size="sm" className="h-9 shrink-0 rounded-lg" onClick={() => setCleared(r.invoice_no, true)} data-testid={`reminder-clear-${r.invoice_no}`}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />Mark cleared
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Page>
  );
}

const SummaryTile = ({ tone, label, value }) => {
  const st = STATE_STYLE[tone];
  return (
    <div className="rounded-2xl border border-border bg-card p-3" data-testid={`reminders-tile-${tone}`}>
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${st.dot}`} />
        <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
};
